import { useState } from 'react';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { LoadingButton } from '@mui/lab';
import { ReactMultiEmail } from 'react-multi-email';
import 'react-multi-email/style.css';
import { useTranslation } from 'react-i18next';
import client from 'client';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  maxWidth: 'calc(100% - 32px)',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  borderRadius: 4,
  p: 4,
};

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function CustomerInvoicesEmailModal({
  open,
  onClose,
  compacctnum,
  jobId,
  filename,
  customerName,
  jobLabel,
  createPdf,
  notify,
}) {
  const { t } = useTranslation('navigation');
  const [emails, setEmails] = useState([]);
  const [scope, setScope] = useState('both');
  const [loading, setLoading] = useState(false);

  const sendEmailData = async () => {
    if (!emails.length) {
      notify(t('EMAIL_IS_REQUIRED'), 'error');
      return;
    }
    if (!compacctnum || jobId == null) {
      notify(t('SOMETHING_WENT_WRONG'), 'error');
      return;
    }
    setLoading(true);
    try {
      const blob = await createPdf(scope);
      const pdf = await blobToBase64(blob);
      const result = await client.post(
        `/invoices/customer/report-email/${compacctnum}/${jobId}`,
        {
          emails,
          pdf,
          filename,
          customerName,
          jobLabel,
          scope,
        },
        { timeout: 0 }
      );
      if (result?.status >= 200 && result?.status < 300) {
        notify(t('MESSAGE_SENT'), 'success');
        onClose();
        return;
      }
      notify(t('SOMETHING_WENT_WRONG'), 'error');
    } catch (error) {
      notify(t('SOMETHING_WENT_WRONG'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      sx={{ zIndex: 1400 }}
    >
      <Box sx={style}>
        <Button
          variant='contained'
          className='absolute top-4 right-4'
          onClick={onClose}
          disabled={loading}
        >
          {t('CLOSE')}
        </Button>
        <Typography
          id='customer-invoices-email-title'
          variant='h4'
          component='h2'
          className='text-center pb-8'
        >
          {t('ENTER_THE_EMAILS')}
        </Typography>
        <RadioGroup
          value={scope}
          onChange={(event) => setScope(event.target.value)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value='open' control={<Radio />} label={t('OPEN_INVOICES')} />
          <FormControlLabel value='paid' control={<Radio />} label={t('PAID_INVOICES')} />
          <FormControlLabel value='both' control={<Radio />} label={t('BOTH')} />
        </RadioGroup>
        <ReactMultiEmail
          placeholder={t('INPUT_YOUR_EMAIL_ADDRESS')}
          emails={emails}
          onChange={setEmails}
          getLabel={(email, index, removeEmail) => (
            <Chip
              className='mr-4'
              data-tag-handle
              label={email}
              onDelete={() => removeEmail(index)}
            />
          )}
        />
        <div className='text-center'>
          <LoadingButton
            loading={loading}
            variant='contained'
            onClick={(event) => {
              event.stopPropagation();
              sendEmailData();
            }}
            className='mx-auto mt-8 cursor-pointer'
          >
            {t('SEND_EMAIL')}
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
}

export default CustomerInvoicesEmailModal;
